<template>
  <Dialog
    v-model:visible="visible"
    header="Send to Court"
    :modal="true"
    :style="{ width: '650px' }"
    @hide="resetForm"
  >
    <div class="court-send-form">
      <div class="info-box mb-2">
        <i class="ri-information-line"></i>
        Property: <strong>{{ property?.property_description || '-' }}</strong>
      </div>

      <div class="form-group">
        <label>Date of Sending: *</label>
        <Calendar
          v-model="form.date_sent"
          dateFormat="yy-mm-dd"
          class="w-full"
          :maxDate="new Date()"
        />
      </div>

      <div class="form-section">
        <h5>Submitting Police Officer</h5>
        <div class="form-group">
          <label>Name: *</label>
          <InputText
            v-model="form.submitting_officer_name"
            placeholder="Enter officer name"
            class="w-full"
          />
        </div>
        <div class="form-group">
          <label>Designation:</label>
          <InputText
            v-model="form.submitting_officer_designation"
            placeholder="e.g., PSI, HC, PC"
            class="w-full"
          />
        </div>
      </div>

      <div class="form-group">
        <label>PR Number:</label>
        <InputText
          v-model="form.pr_number"
          placeholder="Enter PR number"
          class="w-full"
        />
      </div>

      <div class="form-section">
        <h5>Receiving Court Staff</h5>
        <div class="form-group">
          <label>Name: <span v-if="isPrNumberFilled" class="text-danger">*</span></label>
          <InputText
            v-model="form.receiving_court_staff_name"
            placeholder="Enter court staff name"
            class="w-full"
          />
        </div>
        <div class="form-group">
          <label>Designation: <span v-if="isPrNumberFilled" class="text-danger">*</span></label>
          <InputText
            v-model="form.receiving_court_staff_designation"
            placeholder="e.g., Court Reader, Nazir"
            class="w-full"
          />
        </div>
      </div>

      <div class="form-group">
        <label>Remarks:</label>
        <InputText
          v-model="form.remarks"
          placeholder="Enter remarks (optional)"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="ri-close-line"
        @click="visible = false"
        class="p-button-text"
      />
      <Button
        v-if="isEditMode"
        label="Add Court Entry"
        icon="ri-add-line"
        @click="submitCourt(true)"
        :loading="loading"
        class="p-button-secondary"
      />
      <Button
        :label="isEditMode ? 'Update Court' : 'Send to Court'"
        icon="ri-government-line"
        @click="submitCourt(false)"
        :loading="loading"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';

export default {
  name: 'SendToCourtDialog',
  components: {
    Dialog,
    Button,
    Calendar,
    InputText
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    availableQuantity: {
      type: Number,
      required: true
    },
    movements: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      form: {
        date_sent: new Date(),
        submitting_officer_name: '',
        submitting_officer_designation: '',
        receiving_court_staff_name: '',
        receiving_court_staff_designation: '',
        pr_number: '',
        remarks: ''
      },
      currentCourtSubmissionId: null,
      currentSentQuantity: 0
    };
  },
  computed: {
    isWeightBased() {
      const name = (this.property?.property_type?.property_type || this.property?.PropertyType?.property_type || '').toString().toLowerCase();
      const keywords = ['drug', 'gold', 'silver', 'diamond', 'precious metal', 'narcotic', 'grams', 'contraband'];
      return keywords.some(kw => name.includes(kw));
    },
    latestCourtSendRecord() {
      return (this.movements || []).find(m => m.type === 'court_send' && m.court_submission) || null;
    },
    isEditMode() {
      return !!this.latestCourtSendRecord;
    },
    isPrNumberFilled() {
      return !!String(this.form.pr_number || '').trim();
    }
  },
  methods: {
    open() {
      this.visible = true;
      this.resetForm();
      this.prefillFromExistingRecord();
    },
    resetForm() {
      this.form = {
        date_sent: new Date(),
        submitting_officer_name: '',
        submitting_officer_designation: '',
        receiving_court_staff_name: '',
        receiving_court_staff_designation: '',
        pr_number: '',
        remarks: ''
      };
      this.currentCourtSubmissionId = null;
      this.currentSentQuantity = 0;
    },
    prefillFromExistingRecord() {
      const latest = this.latestCourtSendRecord;
      if (!latest || !latest.court_submission) return;
      this.currentCourtSubmissionId = latest.court_submission.court_submission_id || null;
      this.currentSentQuantity = Number(latest.court_submission.quantity_sent || latest.quantity_moved || 0);
      this.form = {
        date_sent: this.toDateOrNow(latest.court_submission.date_sent || latest.movement_date),
        submitting_officer_name: latest.court_submission.submitting_officer_name || '',
        submitting_officer_designation: latest.court_submission.submitting_officer_designation || '',
        receiving_court_staff_name: latest.court_submission.receiving_court_staff_name || '',
        receiving_court_staff_designation: latest.court_submission.receiving_court_staff_designation || '',
        pr_number: latest.court_submission.pr_number || '',
        remarks: latest.remarks || ''
      };
    },
    toDateOrNow(value) {
      if (!value) return new Date();
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return new Date();
      return dt;
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async submitCourt(forceCreate = false) {
      const quantityToSend = Number(this.availableQuantity) || 0;
      const isCreateFlow = !this.isEditMode || forceCreate;

      if (isCreateFlow && (!this.availableQuantity || this.availableQuantity <= 0)) {
        this.$store.dispatch('error', 'No quantity available at PS');
        return;
      }
      if (!this.form.date_sent) {
        this.$store.dispatch('error', 'Please select a date');
        return;
      }

      if (!this.form.submitting_officer_name.trim()) {
        this.$store.dispatch('error', 'Please enter submitting officer name');
        return;
      }

      if (this.isPrNumberFilled && !this.form.receiving_court_staff_name.trim()) {
        this.$store.dispatch('error', 'Please enter receiving court staff name');
        return;
      }

      if (this.isPrNumberFilled && !this.form.receiving_court_staff_designation.trim()) {
        this.$store.dispatch('error', 'Please enter receiving court staff designation');
        return;
      }

      this.loading = true;
      try {
        if (this.isEditMode && !forceCreate) {
          if (!this.currentCourtSubmissionId) {
            this.$store.dispatch('error', 'Unable to identify existing court record');
            return;
          }
          await this.$store.dispatch('post', {
            uri: 'updateCourtSubmission',
            data: {
              property_id: this.property.property_id,
              court_submission_id: this.currentCourtSubmissionId,
              date_sent: this.formatDate(this.form.date_sent),
              submitting_officer_name: this.form.submitting_officer_name,
              submitting_officer_designation: this.form.submitting_officer_designation,
              receiving_court_staff_name: this.form.receiving_court_staff_name,
              receiving_court_staff_designation: this.form.receiving_court_staff_designation,
              is_partial: false,
              balance_quantity: null,
              pr_number: this.form.pr_number,
              remarks: this.form.remarks
            }
          });
          this.$store.dispatch('success', 'Court details updated successfully');
        } else {
          await this.$store.dispatch('post', {
            uri: 'sendPropertyToCourt',
            data: {
              property_id: this.property.property_id,
              quantity_sent: quantityToSend,
              is_partial: false,
              balance_quantity: null,
              date_sent: this.formatDate(this.form.date_sent),
              submitting_officer_name: this.form.submitting_officer_name,
              submitting_officer_designation: this.form.submitting_officer_designation,
              receiving_court_staff_name: this.form.receiving_court_staff_name,
              receiving_court_staff_designation: this.form.receiving_court_staff_designation,
              pr_number: this.form.pr_number,
              remarks: this.form.remarks,
              sync_available_at_ps: this.availableQuantity
            }
          });
          this.$store.dispatch('success', forceCreate ? 'Court entry added successfully' : 'Property sent to court successfully');
        }
        this.$emit('refresh');
        this.visible = false;
      } catch (error) {
        const msg = error.response?.data?.message || '';
        if (msg && (msg.includes('Insufficient quantity') || msg.includes('insufficient'))) {
          this.$store.dispatch('error', 'Insufficient quantity available at PS. If you recently updated the quantity in the form, please save the form first (click Update/Submit), then try sending to court again.');
        } else {
          this.$store.dispatch('error', msg || 'Failed to send property to court');
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.court-send-form {
  padding: 10px 0;
}

.info-box {
  background: #F3E5F5;
  border: 1px solid #9C27B0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6A1B9A;
}

.info-box i {
  font-size: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-section {
  background: #F9F9F9;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 15px;
}

.form-section h5 {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #555;
  font-weight: 600;
}

.w-full {
  width: 100%;
}

</style>
